import moment from 'moment';

import { getImageData } from './pdfGenerator';
import { PDFText, ImageData } from '../models/pdfModal';

/** To format a string to a debit/credit card number format
 *
 * @param cardNumber Can be in any string
 * @returns String in card number format (xxxx xxxx xxxx xxxx)
 */
export const formatCardNumber = (cardNumber: string): string => {
  const number = cardNumber.replace(/ /g, '').substring(0, 16);
  const numberSplit = number.match(/.{1,4}/g);

  return numberSplit ? numberSplit.join(' ') : '';
};

/** To format string to cvv format
 *
 * @param cvv Can be in any string
 * @returns String in cvv format (xxx)
 */
export const formatCVVNumber = (cvv: string): string => {
  return cvv.substring(0, 3);
};

/** Format date in specific format
 *
 * @param dateInput Date item in string
 * @param format The format of the result (Same formatting with momentjs)
 * @returns The string of the date based on the format. If input is empty or is invalid date, then returns empty string
 */
export const formatDate = (
  dateInput: string,
  format: string = 'DD/MM/yyyy'
): string => {
  if (!dateInput) return '';

  const dateMoment = moment(dateInput);
  if (dateMoment.isValid()) {
    return dateMoment.format(format);
  } else {
    return '';
  }
};

/** To convert file to PDFText object (partial)
 * 
 * @param file The file object from input
 * @param maxHeight The max height of the image (Default 15)
 * @param maxWidth The max width of the image (Default 75)
 * @returns Partial PDFText object
 * @example
 * {
    value: The image in base64,
    imageObject: {
      imageHeight: The calculated image height,
      imageWidth: The calculated image width,
      maxHeight: The max height of the image,
      maxWidth: The max width of the image,
    },
    hide: Hide the object if the image is null,
  }
 */
export const formatImageObject = async (
  file: File | null,
  maxHeight: number = 15,
  maxWidth: number = 75
): Promise<Partial<PDFText>> => {
  let imgObject: ImageData | null = null;
  if (file) {
    imgObject = await getImageData(file);
  }

  return {
    value: imgObject?.base64 || '',
    imageObject: {
      imageHeight: imgObject?.height || 0,
      imageWidth: imgObject?.width || 0,
      maxHeight: maxHeight,
      maxWidth: maxWidth,
    },
    hide: !imgObject,
  };
};

export const CLASSES = (...classes: (string | undefined | false | null)[]) => {
  return classes.filter(Boolean).join(' ');
};

/** To compare object and check if there is any difference
 *
 * @param defaultObj The default object
 * @param currentObj The current object to compare to the default
 * @returns true if there is any difference, false if it is the same
 */
export const objectHasDifference = (
  defaultObj: Object,
  currentObj: Object
): boolean => {
  let difference = false;
  const keys = Object.keys(defaultObj);
  let index = 0;
  while (index < keys.length) {
    const key = keys[index];
    if (currentObj[key as keyof typeof currentObj] != undefined) {
      difference =
        currentObj[key as keyof typeof currentObj] !=
        defaultObj[key as keyof typeof defaultObj];
      if (difference) {
        break;
      }
    }
    index++;
  }

  return difference;
};

export const freezeObject = <T>(data: object): T => {
  return JSON.parse(JSON.stringify(data)) as T;
};

/** To create a link (a tag) to download a file from blob
 *
 * @param blob The blob to download
 * @param filename The filename that will be saved
 */
export const downloadFile = (blob: Blob, filename: string) => {
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(link.href);
};

export const blobToBase64 = (blob: Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      resolve(reader.result as string);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};
